import { appConfig } from '@core/config/appConfig';
import { useAuth } from '@core/context/AuthContext';
import { LoadingOverlay } from '@core/ui/StatusFeedback';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

const EMPTY_ARRAY = [];

/**
 * ProtectedRoute - Guard de Rutas Basado en Roles.
 * Verifica autenticación y permisos granulares.
 */
const ProtectedRoute = ({ children, allowedRoles = EMPTY_ARRAY }) => {
    const { user, loading, authError, logout, isTerminalMode } = useAuth();
    const location = useLocation();

    // 1. Mostrar loading con estética industrial si el perfil de usuario aún se está recuperando
    if (loading) {
        return (
            <LoadingOverlay 
                title="Syncing"
                subtitle="Authenticating Security Protocol..."
                variant="dark"
            />
        );
    }

    // 2. Si hay un error crítico de autenticación/perfil, mostrarlo en lugar de redirigir infinitamente
    if (authError) {
        return (
            <div className="h-screen flex flex-col items-center justify-center gap-6 bg-white p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center text-red-600 mb-2">
                    <span className="text-2xl">🚫</span>
                </div>
                <h2 className="text-xl font-black text-primary uppercase italic">Access Denied</h2>
                <p className="text-slate-500 font-bold max-w-md">{authError}</p>
                <button 
                    onClick={logout}
                    className="px-8 py-3 bg-primary text-white font-black rounded-2xl uppercase tracking-widest hover:scale-105 transition-all"
                >
                    Return to Login
                </button>
            </div>
        );
    }

    // 3. Si no hay usuario, redirigir a Login manteniendo la ubicación previa
    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // 3. Bloqueo de Modo Kiosko (Solo permite terminal de retiros y auditoria para admins)
    const isAllowedInKiosk = location.pathname === '/terminal' || (user.role === 'ADMIN' && location.pathname === '/audit');
    
    if (isTerminalMode && !isAllowedInKiosk) {
        console.warn(`Terminal locked in Kiosk Mode: Redirecting to /terminal`);
        return <Navigate to="/terminal" replace />;
    }

    // 4. Si hay roles permitidos definidos, verificar si el usuario tiene uno de ellos
    if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
        console.warn(`ACCESO denegado para rol: ${user.role} en la ruta: ${location.pathname}`);
        // Si no es admin, su lugar es la terminal de retiros
        return <Navigate to="/terminal" replace />;
    }

    // 4. Todo correcto, renderizar hijos
    return children;
};

export default ProtectedRoute;
